'use es6';

import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import filter from 'transmute/filter';
import get from 'transmute/get';
import sortBy from 'transmute/sortBy';
import valueSeq from 'transmute/valueSeq';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PermissionSettingsContext } from './context/PermissionSettingsContext';
import TeamCheckboxGroup from './TeamCheckboxGroup';
import UserCheckboxGroup from './UserCheckboxGroup';
import EntityTagGroup, { EntityType } from './EntityTagGroup';
import { useTeams, useUserSetInTeamIdSet } from '../../../data/hooks/useTeams';
import { useTeamHierarchy } from '../../../data/hooks/useTeamHierarchy';
import { useUsers } from '../../../data/hooks/useUsers';
import { getName } from '../../../data/schemas/teamSchema';
import { getEmail } from '../../../data/schemas/userSchema';
import { getUserLabel } from '../../../lib/user-logic';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SpecificPartitioningControl = ({
  defaultSearchValue = ''
}) => {
  const {
    data: teamMap
  } = useTeams();
  const {
    data: teamHierarchy
  } = useTeamHierarchy();
  const {
    data: userMap
  } = useUsers();
  const userSeq = valueSeq(userMap);
  const {
    selectedUserSet,
    selectedTeamSet,
    selectUser,
    deselectUser,
    selectTeam,
    deselectTeam
  } = useContext(PermissionSettingsContext);
  const selectedTeamMemberUserSet = useUserSetInTeamIdSet(selectedTeamSet);
  const [searchInput, setSearchInput] = useState(defaultSearchValue);
  const filterUserBySearch = useCallback(user => getUserLabel({
    user
  }).toLocaleLowerCase().includes(searchInput), [searchInput]);
  const filterTeamBySearch = useCallback(team => {
    const teamName = getName(team) || '';
    return teamName.toLocaleLowerCase().includes(searchInput);
  }, [searchInput]);
  const userSeqSorted = useMemo(() => sortBy(getEmail, userSeq), [userSeq]);
  const displayedUserSeq = useMemo(() => filter(filterUserBySearch, userSeqSorted), [filterUserBySearch, userSeqSorted]);
  const onUserCheckboxChange = useCallback(({
    checked,
    value
  }) => {
    if (checked) {
      selectUser(value);
    } else {
      deselectUser(value);
    }
  }, [selectUser, deselectUser]);
  const onTeamCheckboxChange = useCallback(({
    checked,
    value
  }) => {
    if (checked) {
      selectTeam(value);
    } else {
      deselectTeam(value);
    }
  }, [selectTeam, deselectTeam]);
  const deselectableUserSet = selectedUserSet.subtract(selectedTeamMemberUserSet);

  // Fetching permissions for an asset returns the child-most teams
  // for access. We mimic selecting these child teams upon mount of
  // the permission dialogue to ensure that parent teams are rendered
  // as selected as well.
  useEffect(() => {
    for (const childTeamId of selectedTeamSet) {
      selectTeam(childTeamId);
    }
  }, [selectedTeamSet, selectTeam]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.permissions.specificPartitioning.label"
      }),
      "aria-label": "assignee-tag-group",
      children: /*#__PURE__*/_jsx(EntityTagGroup, {
        userValues: deselectableUserSet.map(id => ({
          value: id,
          name: getUserLabel({
            user: get(id, userMap),
            showEmail: false
          }) || id
        })),
        teamValues: selectedTeamSet.map(id => ({
          value: id,
          name: getName(get(id, teamMap)) || id
        })),
        onCloseClick: ({
          value,
          type
        }) => {
          if (type === EntityType.USER) {
            onUserCheckboxChange({
              value
            });
          } else if (type === EntityType.TEAM) {
            onTeamCheckboxChange({
              value
            });
          }
        }
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('reporting-action-components.permissions.specificPartitioning.search'),
      children: /*#__PURE__*/_jsx(SearchInput, {
        value: searchInput,
        onChange: ({
          target: {
            value
          }
        }) => {
          setSearchInput(value);
        }
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.permissions.specificPartitioning.teams"
      }),
      children: /*#__PURE__*/_jsx(TeamCheckboxGroup, {
        teamCollection: teamHierarchy,
        filter: filterTeamBySearch,
        value: selectedTeamSet,
        onChange: onTeamCheckboxChange
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.permissions.specificPartitioning.users"
      }),
      children: /*#__PURE__*/_jsx(UserCheckboxGroup, {
        userCollection: displayedUserSeq,
        selectedTeamMemberValues: selectedTeamMemberUserSet,
        selectedValues: selectedUserSet,
        onChange: onUserCheckboxChange
      })
    })]
  });
};
SpecificPartitioningControl.propTypes = {
  defaultSearchValue: PropTypes.string
};
export const SpecificPartitioningControlWrapper = props => {
  const {
    loading: teamsLoading
  } = useTeams();
  const {
    loading: teamHierarchyLoading
  } = useTeamHierarchy();
  const {
    loading: usersLoading
  } = useUsers();
  if (teamsLoading || teamHierarchyLoading || usersLoading) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(FormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.permissions.specificPartitioning.label"
        })
      }), /*#__PURE__*/_jsx(LoadingSpinner, {})]
    });
  }
  return /*#__PURE__*/_jsx(SpecificPartitioningControl, Object.assign({}, props));
};
export default SpecificPartitioningControlWrapper;